'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

// Gamepad API "standard" mapping: button index → position. Labels differ per brand, so the
// controller id picks a label set.
const LABELS = {
  xbox: [
    'A',
    'B',
    'X',
    'Y',
    'LB',
    'RB',
    'LT',
    'RT',
    'View',
    'Menu',
    'LS',
    'RS',
    '↑',
    '↓',
    '←',
    '→',
    'Xbox',
  ],
  playstation: [
    '✕',
    '○',
    '□',
    '△',
    'L1',
    'R1',
    'L2',
    'R2',
    'Create',
    'Options',
    'L3',
    'R3',
    '↑',
    '↓',
    '←',
    '→',
    'PS',
  ],
  nintendo: [
    'B',
    'A',
    'Y',
    'X',
    'L',
    'R',
    'ZL',
    'ZR',
    '−',
    '+',
    'LS',
    'RS',
    '↑',
    '↓',
    '←',
    '→',
    'Home',
  ],
} as const;

type Brand = keyof typeof LABELS;

function brandOf(id: string): Brand {
  if (/054c|dualsense|dualshock|wireless controller|playstation/i.test(id)) return 'playstation';
  if (/057e|nintendo|pro controller|joy-con/i.test(id)) return 'nintendo';
  return 'xbox';
}

type Pad = {
  index: number;
  id: string;
  mapping: string;
  buttons: { pressed: boolean; value: number }[];
  axes: number[];
  canRumble: boolean;
};

const BINS = 48;

function Stick({ x, y, label, ring }: { x: number; y: number; label: string; ring: number[] }) {
  const r = 60;
  const pts = ring
    .map((m, i) => {
      const a = (i / BINS) * Math.PI * 2;
      return `${70 + Math.cos(a) * m * r},${70 + Math.sin(a) * m * r}`;
    })
    .join(' ');
  const mag = Math.hypot(x, y);
  return (
    <figure style={{ margin: 0, textAlign: 'center' }}>
      <svg
        aria-label={`${label} position`}
        role="img"
        style={{ width: 140, height: 140 }}
        viewBox="0 0 140 140"
      >
        <circle cx="70" cy="70" fill="none" r={r} stroke="currentColor" strokeOpacity="0.35" />
        <circle
          cx="70"
          cy="70"
          fill="none"
          r={r * 0.1}
          stroke="currentColor"
          strokeDasharray="2 2"
          strokeOpacity="0.5"
        />
        <line stroke="currentColor" strokeOpacity="0.15" x1="10" x2="130" y1="70" y2="70" />
        <line stroke="currentColor" strokeOpacity="0.15" x1="70" x2="70" y1="10" y2="130" />
        {ring.some((m) => m > 0) ? (
          <polygon
            fill="var(--accent-soft, rgba(37,99,235,0.12))"
            points={pts}
            stroke="var(--accent, #2563eb)"
            strokeWidth="1"
          />
        ) : null}
        <circle cx={70 + x * r} cy={70 + y * r} fill="var(--accent, #2563eb)" r="7" />
      </svg>
      <figcaption style={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem' }}>
        {label}: X {x.toFixed(3)} · Y {y.toFixed(3)} · {Math.round(mag * 100)}%
      </figcaption>
    </figure>
  );
}

export default function BrowserGamepadTester({ config }: { config?: { toolSlug?: string } }) {
  const [pads, setPads] = useState<Pad[]>([]);
  const [active, setActive] = useState(0);
  const [supported, setSupported] = useState(true);
  const [rings, setRings] = useState<number[][]>([Array(BINS).fill(0), Array(BINS).fill(0)]);
  const ringsRef = useRef<number[][]>([Array(BINS).fill(0), Array(BINS).fill(0)]);
  const [rumbleNote, setRumbleNote] = useState<string | null>(null);
  const reported = useRef(false);
  const lastKey = useRef('');

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('getGamepads' in navigator)) {
      setSupported(false);
      return;
    }
    let raf = 0;
    let frame = 0;
    const loop = () => {
      const list = Array.from(navigator.getGamepads?.() ?? []).filter((g): g is Gamepad =>
        Boolean(g),
      );
      if (list.length && !reported.current) {
        reported.current = true;
        void consumeToolUsage(config?.toolSlug ?? 'gamepad-tester', '').catch(() => undefined);
      }
      const snapshot = list.map((g) => ({
        index: g.index,
        id: g.id,
        mapping: g.mapping,
        buttons: g.buttons.map((b) => ({ pressed: b.pressed, value: b.value })),
        axes: [...g.axes],
        canRumble: Boolean((g as Gamepad & { vibrationActuator?: unknown }).vibrationActuator),
      }));
      // Circularity: remember the furthest reach in each direction while the user
      // rotates the stick around its edge.
      const g = list[0] && (list.find((p) => p.index === active) ?? list[0]);
      if (g) {
        for (let s = 0; s < 2; s += 1) {
          const x = g.axes[s * 2] ?? 0;
          const y = g.axes[s * 2 + 1] ?? 0;
          const m = Math.hypot(x, y);
          if (m > 0.5) {
            const bin =
              Math.floor(
                ((Math.atan2(y, x) + Math.PI * 2) % (Math.PI * 2)) / ((Math.PI * 2) / BINS),
              ) % BINS;
            ringsRef.current[s][bin] = Math.max(ringsRef.current[s][bin], Math.min(m, 1.5));
          }
        }
      }
      // Re-render only when something moved: an idle page must not repaint 60 times a second.
      const key = JSON.stringify(snapshot);
      if (key !== lastKey.current) {
        lastKey.current = key;
        setPads(snapshot);
      }
      frame += 1;
      if (g && frame % 6 === 0) setRings(ringsRef.current.map((r) => [...r]));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active, config?.toolSlug]);

  const pad = pads.find((p) => p.index === active) ?? pads[0];
  const brand = pad ? brandOf(pad.id) : 'xbox';
  const labels = LABELS[brand];

  const circularity = (ring: number[]) => {
    const filled = ring.filter((m) => m > 0);
    if (filled.length < BINS * 0.9) return null;
    return (filled.reduce((s, m) => s + Math.abs(1 - Math.min(m, 1.2)), 0) / filled.length) * 100;
  };

  async function rumble(strong: number, weak: number, ms: number) {
    const g = navigator.getGamepads?.()[pad?.index ?? 0] as
      | (Gamepad & {
          vibrationActuator?: { playEffect: (t: string, p: object) => Promise<string> };
        })
      | null;
    if (!g?.vibrationActuator) {
      setRumbleNote('This browser or controller does not expose vibration.');
      return;
    }
    try {
      await g.vibrationActuator.playEffect('dual-rumble', {
        duration: ms,
        strongMagnitude: strong,
        weakMagnitude: weak,
      });
      setRumbleNote(null);
    } catch {
      setRumbleNote('The controller refused the vibration request.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Controller</span>
        <h2 className="tool-live-title">Gamepad tester</h2>
        <p className="tool-live-subtitle">
          Connect a controller by USB or Bluetooth and press any button. Works with Xbox,
          PlayStation, Switch Pro and most PC gamepads.
        </p>
      </div>
      {!supported ? (
        <p className="tool-live-panel-note">
          This browser does not support the Gamepad API. Try a current Chrome, Edge, Firefox or
          Safari.
        </p>
      ) : !pad ? (
        <div className="tool-live-preview" style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          <p className="tool-live-title" style={{ margin: 0 }}>
            Press any button on your controller
          </p>
          <p className="tool-live-panel-note">
            Browsers only reveal a controller after a button press, for privacy. If nothing appears,
            reconnect it and press a face button, not just the stick.
          </p>
        </div>
      ) : (
        <div className="tool-live-grid">
          <section className="tool-live-uploader">
            {pads.length > 1 ? (
              <label className="tool-live-field">
                <span>Controller</span>
                <select onChange={(e) => setActive(Number(e.target.value))} value={pad.index}>
                  {pads.map((p) => (
                    <option key={p.index} value={p.index}>
                      #{p.index + 1} {p.id.slice(0, 40)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <p className="tool-live-panel-note" style={{ wordBreak: 'break-word' }}>
              <strong>{pad.id}</strong>
              <br />
              {pad.buttons.length} buttons, {pad.axes.length} axes, mapping:{' '}
              {pad.mapping || 'non-standard (labels may not match)'}
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(4.2rem, 1fr))',
                gap: '0.4rem',
              }}
            >
              {pad.buttons.map((b, i) => (
                <div
                  key={i}
                  style={{
                    border: '1px solid currentColor',
                    borderRadius: 6,
                    padding: '0.35rem 0.25rem',
                    textAlign: 'center',
                    fontVariantNumeric: 'tabular-nums',
                    background: b.pressed
                      ? 'var(--accent, #2563eb)'
                      : b.value > 0
                        ? `rgba(37,99,235,${0.15 + b.value * 0.5})`
                        : 'transparent',
                    color: b.pressed ? '#fff' : 'inherit',
                    transition: 'background 60ms',
                  }}
                >
                  <div style={{ fontWeight: 700 }}>{labels[i] ?? `B${i}`}</div>
                  <div style={{ fontSize: '0.75rem' }}>{b.value.toFixed(2)}</div>
                </div>
              ))}
            </div>
            {pad.canRumble ? (
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => rumble(1, 1, 800)}
                  type="button"
                >
                  Vibrate (both motors)
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => rumble(1, 0, 600)}
                  type="button"
                >
                  Strong motor
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => rumble(0, 1, 600)}
                  type="button"
                >
                  Weak motor
                </button>
              </div>
            ) : null}
            {rumbleNote ? <p className="tool-live-panel-note">{rumbleNote}</p> : null}
          </section>
          <section className="tool-live-preview">
            <div
              style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center' }}
            >
              <Stick label="Left stick" ring={rings[0]} x={pad.axes[0] ?? 0} y={pad.axes[1] ?? 0} />
              <Stick
                label="Right stick"
                ring={rings[1]}
                x={pad.axes[2] ?? 0}
                y={pad.axes[3] ?? 0}
              />
            </div>
            {[0, 1].map((s) => {
              const x = pad.axes[s * 2] ?? 0;
              const y = pad.axes[s * 2 + 1] ?? 0;
              const m = Math.hypot(x, y);
              const c = circularity(rings[s]);
              return (
                <p className="tool-live-panel-note" key={s}>
                  <strong>{s ? 'Right' : 'Left'} stick:</strong>{' '}
                  {m < 0.02
                    ? 'centred, no drift.'
                    : m < 0.1
                      ? `resting ${Math.round(m * 100)}% off centre. If you are not touching it, that is drift; most games hide under 10% with a deadzone.`
                      : 'moving.'}{' '}
                  {c === null
                    ? 'Rotate it slowly around the edge to measure circularity.'
                    : `Circularity error ${c.toFixed(1)}% (under 10% is good).`}
                </p>
              );
            })}
            <div className="tool-live-actions">
              <button
                className="button button-secondary"
                onClick={() => {
                  ringsRef.current = [Array(BINS).fill(0), Array(BINS).fill(0)];
                  setRings(ringsRef.current.map((r) => [...r]));
                }}
                type="button"
              >
                Reset circularity
              </button>
            </div>
            {pad.axes.length > 4 ? (
              <p className="tool-live-panel-note" style={{ fontVariantNumeric: 'tabular-nums' }}>
                Extra axes:{' '}
                {pad.axes
                  .slice(4)
                  .map((a, i) => `A${i + 4} ${a.toFixed(2)}`)
                  .join(' · ')}
              </p>
            ) : null}
          </section>
        </div>
      )}
    </div>
  );
}
